import { useCallback, useRef, useState, type TouchEvent } from 'react';

export const LONG_PRESS_MS = 450;

/**
 * Long-press 450 мс на тач-устройствах (README «Mobile»): карточка сжимается,
 * `navigator.vibrate(12)`, открывается action sheet; следующий click подавляется.
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);
  const [pressing, setPressing] = useState(false);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPressing(false);
  }, []);

  const onTouchStart = useCallback(
    (_event: TouchEvent) => {
      cancel();
      fired.current = false;
      setPressing(true);
      timer.current = setTimeout(() => {
        fired.current = true;
        setPressing(false);
        try {
          navigator.vibrate?.(12);
        } catch {
          // без вибрации
        }
        onLongPress();
      }, LONG_PRESS_MS);
    },
    [cancel, onLongPress],
  );

  const onContextMenu = useCallback((event: { preventDefault: () => void }) => {
    if (window.matchMedia('(hover: none)').matches) event.preventDefault();
  }, []);

  /** true, если click пришёл после сработавшего long-press и должен быть проигнорирован. */
  const consumeLongPress = useCallback(() => {
    if (!fired.current) return false;
    fired.current = false;
    return true;
  }, []);

  return {
    pressing,
    handlers: {
      onTouchStart,
      onTouchMove: cancel,
      onTouchEnd: cancel,
      onTouchCancel: cancel,
      onContextMenu,
    },
    consumeLongPress,
  };
}
